import dayjs from "dayjs";
import {isEmpty, isNil, map} from "lodash";
import React from "react";
import {MilitaryInfo} from "../../../consumer/declarations";
import {
  AccountUpdateData,
  ActivityData,
  ActivityDetails,
  ChannelPreferenceUpdateData,
  CollectionNoticeTriggerData,
  CommentData,
  ConsumerDetailsUpdateData,
  ContactDetailsUpdateData,
  ContactUsData,
  CreditData,
  CreditReportData,
  DebitData,
  DisputeData,
  DSCData,
  EmailData,
  EnumInfo,
  FlagUpdateData,
  LegalCollectionsData,
  LetterData,
  PartnerActionData,
  PaymentData,
  PaymentPlanData,
  PhoneCallData,
  ReminderData,
  RepresentationUpdateData,
  SMSData,
  StatuteOfLimitationsUpdateData,
  UserActivityData,
  WebchatData,
  WhatsAppData,
} from "../../../declarations";
import {AccountUpdateAction, ChannelType, PaymentPlanFrequency} from "../../../enums";
import {
  CreditorRecalledReason,
  creditorRecalledReasonLabels,
  RecallStatus,
  recallStatusLabels,
} from "../../../enums/creditor_recall";
import {convertUTCDateToTimezone, DATE_FORMAT_V2, formatCurrency, InputUtils} from "../../../helpers";
import {IconApproved, IconCall, IconDecline, IconEmail, IconLetter, IconSms} from "../../assets";
import {Colors} from "../../colors";
import {Chip} from "../Chip";
import {Container} from "../Container";
import {LegalCollectionSubtitle} from "./LegalCollectionSubtitle";
import {StyledText} from "./StyledText";
import {
  accountStatusTypes,
  actionToTextMap,
  allFlagTypes,
  getChipInfo,
  getContactIconAndLabel,
  getPaymentPlanSubtitleByType,
} from "./utils";

const actionLabelMap: {[key in AccountUpdateAction]: string} = {
  [AccountUpdateAction.ADD]: "A value was added to",
  [AccountUpdateAction.DELETE]: "A value was deleted from",
  [AccountUpdateAction.UPDATE]: "Update in",
} as const;

export const planTypeToLabelMap: Map<PaymentPlanFrequency, EnumInfo> = new Map<PaymentPlanFrequency, EnumInfo>([
  ["weekly", {labelKey: "Fixed installments plan"}],
  ["bi_weekly", {labelKey: "Fixed installments plan"}],
  ["monthly", {labelKey: "Fixed installments plan"}],
  ["one_time", {labelKey: "One-time payment"}],
  ["first_and_fifteenth", {labelKey: "Fixed installments plan"}],
  ["non_linear", {labelKey: "Custom plan"}],
]);

export const ChannelToSubtitleMap: Map<ChannelType, (activity: ActivityData) => ActivityDetails> = new Map<
  ChannelType,
  (activity: ActivityData) => ActivityDetails
>([
  ["phone_call", (activity: ActivityData) => getPhoneCallSubtitle(activity)],
  ["sms", (activity: ActivityData) => getSMSSubtitle(activity)],
  ["email", (activity: ActivityData) => getEmailSubtitle(activity)],
  ["letter", (activity: ActivityData) => getLetterSubtitle(activity)],
  ["webchat", (activity: ActivityData) => getWebchatSubtitle(activity)],
  ["webchat_offline", (activity: ActivityData) => getWebchatSubtitle(activity)],
  ["payment_plan", (activity: ActivityData) => getPaymentPlanSubtitle(activity)],
  ["payment", (activity: ActivityData) => getPaymentSubtitle(activity)],
  ["account_update", (activity: ActivityData) => getAccountUpdateSubtitle(activity)],
  ["creditor_recall_update", (activity: ActivityData) => getCreditorRecallUpdateSubtitle(activity)],
  ["contact_us", (activity: ActivityData) => getContactUsSubtitle(activity)],
  ["dispute", (activity: ActivityData) => getDisputeSubtitle(activity)],
  ["user_activity", (activity: ActivityData) => getUserActivitySubtitle(activity)],
  ["trigger", (activity: ActivityData) => getCollectionNoticeTriggerSubtitle(activity)],
  ["comment", (activity: ActivityData) => getCommentSubtitle(activity)],
  ["credit_report", (activity: ActivityData) => getCreditReportSubtitle(activity)],
  ["reminder", (activity: ActivityData) => getReminderSubtitle(activity)],
  ["credit", (activity: ActivityData) => getCreditSubtitle(activity)],
  ["debit", (activity: ActivityData) => getDebitSubtitle(activity)],
  ["channel_preferences_update", (activity: ActivityData) => getChannelPreferenceUpdateSubtitle(activity)],
  ["contact_details_update", (activity: ActivityData) => getContactDetailsUpdateSubtitles(activity)],
  ["flag_update", (activity: ActivityData) => getFlagUpdateSubtitles(activity)],
  ["statute_of_limitation_update", (activity: ActivityData) => getStatuteOfLimitationsUpdateSubtitles(activity)],
  ["consumer_details_update", (activity: ActivityData) => getConsumerDetailsUpdateSubtitles(activity)],
  ["representation_update", (activity: ActivityData) => getRepresentationUpdateSubtitles(activity)],
  ["document_request", () => getDocumentRequestSubtitle()],
  ["partner_action", (activity: ActivityData) => getPartnerPlatformSubtitles(activity)],
  ["legal_collections", (activity: ActivityData) => getLegalCollectionsSubtitle(activity)],
  ["whatsapp", (activity: ActivityData) => getWhatsAppSubtitle(activity)],
  ["dsc", (activity: ActivityData) => getDSCSubtitle(activity)],
]);

const getPartnerPlatformSubtitles = (activity: ActivityData): ActivityDetails => {
  const {event_type} = activity as PartnerActionData;
  const eventSubTitle: Record<string, string> = {
    PARTNER_PAYMENT_SUBMITTED: "Direct payment update",
    PARTNER_CREDITOR_RECALLED: "Recall request",
    CREDITOR_ADJUSTMENT: "Creditor adjustment",
  };
  const subtitle = eventSubTitle[event_type] ?? "";
  const disable = !subtitle;

  return {subtitle, disable};
};

const getVoiceAISubtitle = (activity: ActivityData): ActivityDetails => {
  const {voice_ai_outcome, call_outcome, call_summary} = activity as PhoneCallData;
  const entries = voice_ai_outcome ? Object.entries(voice_ai_outcome).filter(([, v]) => !isNil(v)) : [];
  const disable = isEmpty(entries) && !call_summary;
  if (!isEmpty(call_outcome)) {
    const prettifiedOutcomes = map(call_outcome, (outcome) => InputUtils.prettierSnakeCase(outcome));
    return {subtitle: `Call outcome: ${prettifiedOutcomes.join(", ")}`, disable};
  }
  const subtitle = isEmpty(entries) ? "Not Available" : entries.map(([key, value]) => `${key}: ${value}`).join(", ");
  return {subtitle: `Call outcome: ${subtitle}`, disable};
};

const getPhoneCallSubtitle = (activity: ActivityData): ActivityDetails => {
  const {event_type, call_outcome, call_reason} = activity as PhoneCallData;
  let subtitle;
  let activityDetails;
  if (event_type === "VOICE_AI_CALL") {
    activityDetails = getVoiceAISubtitle(activity);
  } else if (event_type === "SCHEDULED_PHONE_CALL") {
    activityDetails = {subtitle: "", disable: false};
  } else if (event_type === "PHONE_CALL_MISSED_INCOMING") {
    subtitle = "No answer";
  } else if (!isEmpty(call_outcome)) {
    const prettifiedOutcomes = map(call_outcome, (outcome) => InputUtils.prettierSnakeCase(outcome));
    subtitle = prettifiedOutcomes.join(", ");
  } else {
    subtitle = "Not Available";
  }
  const disable = !call_outcome && !call_reason;
  return activityDetails || {subtitle: `Call outcome: ${subtitle}`, disable};
};

const getWhatsAppSubtitle = (activity: ActivityData): ActivityDetails => {
  const {message} = activity as WhatsAppData;
  const subtitle = isEmpty(message) ? "" : `Message: ${message}`;
  return {subtitle, disable: false};
};

const getSMSSubtitle = (activity: ActivityData): ActivityDetails => {
  const {message} = activity as SMSData;
  const subtitle = isEmpty(message) ? "" : `Message: ${message}`;
  return {subtitle, disable: false};
};

const getEmailSubtitle = (activity: ActivityData): ActivityDetails => {
  const {subject, to_email, email_file_id, attachments, body, customer_io_payload} = activity as EmailData;
  const disable = !to_email && !email_file_id && isEmpty(attachments) && !body && !customer_io_payload;
  const subtitle = isEmpty(subject) ? "" : `Message: ${subject}`;
  return {subtitle, disable};
};

const getLetterSubtitle = (activity: ActivityData): ActivityDetails => {
  const {letter_file_id, recipient, letter_type} = activity as LetterData;
  const subtitle = letter_type ? `Type: ${letter_type}` : "";
  const disable = !letter_file_id && !recipient;
  return {subtitle, disable};
};

const getWebchatSubtitle = (activity: ActivityData): ActivityDetails => {
  const {num_of_messages, chat_outcome, chat_summary} = activity as WebchatData;
  let subtitle = "No outcome";
  if (!isEmpty(chat_outcome)) {
    const prettifiedOutcomes = map(chat_outcome, (outcome) => InputUtils.prettierSnakeCase(outcome));
    subtitle = "Chat outcome: " + prettifiedOutcomes.join(", ");
  }
  const disable = !num_of_messages && !chat_summary;
  return {subtitle, disable};
};

const getPaymentPlanSubtitle = (activity: ActivityData): ActivityDetails => {
  const {installment_frequency, payment_plan_id, payment_plan_type, event_type, new_start_date, source} =
    activity as PaymentPlanData;

  if (event_type === "PAYMENT_PLAN_RESCHEDULED" && new_start_date) {
    const formattedDate = convertUTCDateToTimezone(new_start_date, "date");
    return {subtitle: `Next payment date changed to ${formattedDate}`, disable: true};
  }

  const subtitle = getPaymentPlanSubtitleByType({
    paymentPlanType: payment_plan_type,
    frequency: installment_frequency,
  });
  const sourceLabel = source === "voice_ai" ? " | Source: Voice AI" : "";
  return {subtitle: `Type: ${subtitle}${sourceLabel}`, disable: !payment_plan_id};
};

const getPaymentSubtitle = (activity: ActivityData): ActivityDetails => {
  const {paid_to, payment_plan_id, amount, original_due_date, new_due_date, event_type} = activity as PaymentData;
  const subtitle = `Amount: ${amount ? formatCurrency(amount) : "Unknown"}`;
  const updateInstallmentDueDateDisableCondition = !original_due_date && !new_due_date;
  const otherPaymentsDisableCondition = !paid_to && !payment_plan_id;
  const disable =
    event_type === "INSTALLMENT_DUE_DATE_UPDATED"
      ? updateInstallmentDueDateDisableCondition
      : otherPaymentsDisableCondition;
  return {subtitle, disable};
};
const getCreditSubtitle = (activity: ActivityData): ActivityDetails => {
  if (!isCreditData(activity)) {
    console.error("Expected CreditData, but received incompatible activity:", activity);
    return {subtitle: "Invalid data", disable: true};
  }
  const {public_id, amount} = activity as CreditData;
  const subtitle = `Amount: ${formatCurrency(amount)}`;

  return {subtitle, disable: isEmpty(public_id)};
};

const getDebitSubtitle = (activity: ActivityData): ActivityDetails => {
  const {amount, public_id} = activity as DebitData;
  const subtitle = `Amount: ${formatCurrency(amount)}`;
  return {subtitle, disable: isEmpty(public_id)};
};

const isCreditData = (activity: ActivityData): activity is CreditData => {
  return "amount" in activity;
};

const getAccountUpdateSubtitle = (activity: ActivityData): ActivityDetails => {
  const {action, section, diff} = activity as AccountUpdateData;
  const subtitle = `${actionLabelMap[action as AccountUpdateAction]} ${section}`;
  return {subtitle, disable: isEmpty(diff)};
};

const getCreditorRecallUpdateSubtitle = (activity: ActivityData): ActivityDetails => {
  const flagData = activity as any;
  const creditorRecallChanges = flagData.creditor_recall_changes || {};
  const creditorRecallData = flagData.creditor_recall_data;

  if (isEmpty(creditorRecallChanges)) {
    return {subtitle: "Creditor recall updated", disable: false};
  }

  const formatValue = (value: any): string => {
    if (typeof value === "string" && dayjs(value, DATE_FORMAT_V2, true).isValid()) {
      return dayjs(value).format(DATE_FORMAT_V2);
    }
    if (typeof value === "string") {
      const labelInRecallReason = creditorRecalledReasonLabels[value as CreditorRecalledReason];
      const labelInRecallStatus = recallStatusLabels[value as RecallStatus];
      return labelInRecallReason || labelInRecallStatus || InputUtils.prettierSnakeCaseSentence(value);
    }
    return String(value);
  };

  const parts = Object.entries(creditorRecallChanges)
    .filter(([_, value]) => value != null)
    .map(
      ([key, value]) =>
        `${InputUtils.prettierSnakeCase(key)} changed to: ${
          key === "buyback_bank_transaction_id" ? String(value) : formatValue(value)
        }`
    );

  return {subtitle: parts.join(", "), disable: !creditorRecallData || isEmpty(creditorRecallData)};
};

const getContactUsSubtitle = (activity: ActivityData): ActivityDetails => {
  const {text, email} = activity as ContactUsData;
  const subtitle = `Message: ${isEmpty(text) ? "no content" : text}`;
  return {subtitle, disable: !email};
};

const getDisputeSubtitle = (activity: ActivityData): ActivityDetails => {
  const {dispute_type, dispute_additional_text, files_names} = activity as DisputeData;
  const subtitle = dispute_type ? `Type: ${InputUtils.prettierSnakeCase(dispute_type) ?? "Unknown"}` : "";
  const disable = !dispute_additional_text && isEmpty(files_names);
  return {subtitle, disable};
};

const getCollectionNoticeTriggerSubtitle = (activity: ActivityData): ActivityDetails => {
  const {trigger_type} = activity as CollectionNoticeTriggerData;
  const subtitle = `Type: ${InputUtils.prettierSnakeCase(trigger_type)}`;
  return {subtitle, disable: false};
};

const getUserActivitySubtitle = (activity: ActivityData): ActivityDetails => {
  const {event_type, payment_plan_offers, questionnaire_answers} = activity as UserActivityData;
  const subtitle = InputUtils.prettierSnakeCase(event_type);
  const expandable =
    event_type === "USER_SUBMITTED_QUESTIONNAIRE" ? !isEmpty(questionnaire_answers) : !isEmpty(payment_plan_offers);
  return {subtitle: `Event: ${subtitle}`, disable: !expandable};
};

const getCommentSubtitle = (activity: ActivityData): ActivityDetails => {
  const {comment} = activity as CommentData;
  const subtitle = `Comment: ${comment}`;
  return {subtitle, disable: false};
};
const getCreditReportSubtitle = (activity: ActivityData): ActivityDetails => {
  const {action, report_data, no_report_data} = activity as CreditReportData;
  const actionSubTitle = {
    create_statement: "Report Furnished",
    delete_account: "Delete Account",
    close_account: "Close Account",
    no_report: "No Report",
    report_dispute_started: "Report Dispute Started",
    report_dispute_completed: "Report Dispute Completed",
    compliance_condition_reported: "Compliance Condition Reported",
  }[action];
  const subtitle = `Action: ${actionSubTitle}`;
  const {compliance_condition_data} = activity as CreditReportData;
  const hasExpandableContent =
    !!report_data || (no_report_data && no_report_data.length > 0) || !!compliance_condition_data;
  return {subtitle, disable: !hasExpandableContent};
};
const getReminderSubtitle = (activity: ActivityData): ActivityDetails => {
  const {note} = activity as ReminderData;
  const subtitle = note;
  return {subtitle, disable: false};
};

export const getContactDetailsUpdateSubtitles = (activity: ActivityData): ActivityDetails => {
  const data = activity as ContactDetailsUpdateData;
  const {icon, label} = getContactIconAndLabel(data.contact_type);
  const IconComponent = icon;

  if (data.action === "update" && getChipInfo(data)) {
    const {label: chipLabel, variant} = getChipInfo(data)!;
    return {
      subtitle: (
        <Container row sx={{gap: "4px"}}>
          <IconComponent color={Colors.grey._1000} width={20} height={20} />
          <StyledText bold value={data.prettified_entity_value} sx={{marginLeft: "2px"}} />
          <StyledText value=" changed to: " />
          <Chip label={chipLabel} variant_={variant} size_="small" border />
        </Container>
      ),
      disable: true,
    };
  }

  return {
    subtitle: (
      <Container row sx={{gap: "4px"}}>
        <IconComponent color={Colors.grey._1000} width={20} height={20} />
        <StyledText value={`${label} ${actionToTextMap[data.action || ""]}: `} sx={{marginLeft: "2px"}} />
        <StyledText bold value={data.prettified_entity_value} />
      </Container>
    ),
    disable: true,
  };
};

export const getChannelPreferenceUpdateSubtitle = (activity: ActivityData): ActivityDetails => {
  const prefs = activity as ChannelPreferenceUpdateData;
  const mapping: [keyof ChannelPreferenceUpdateData, string, any][] = [
    ["sms_preferences", "SMS", IconSms],
    ["email_preferences", "Emails", IconEmail],
    ["mail_preferences", "Letters", IconLetter],
    ["phone_call_preferences", "Phone calls", IconCall],
  ];

  for (const [key, label, Icon] of mapping) {
    const value = prefs[key];
    if (value != null) {
      return {
        subtitle: (
          <Container row sx={{gap: "4px"}}>
            <Icon color={Colors.grey._1000} width={20} height={20} />
            <StyledText value={` ${label} changed to `} sx={{marginLeft: "2px"}} />
            <StyledText value={value ? "Opt in" : "Opt out"} bold />
          </Container>
        ),
        disable: true,
      };
    }
  }

  return {subtitle: <StyledText value="Unknown preference change" />, disable: true};
};

export const getFlagUpdateSubtitles = (activity: ActivityData): ActivityDetails => {
  const data = activity as FlagUpdateData;
  const creditorRecallData = data.creditor_recall_data;

  const statusTypeMap = Object.fromEntries(
    accountStatusTypes.map(({value, label, variant}) => [value, {label, variant}])
  );
  if (data.status && statusTypeMap[data.status]) {
    const {label, variant} = statusTypeMap[data.status];
    return {
      subtitle: (
        <Container row sx={{gap: "4px"}}>
          <StyledText value="Account changed to: " />
          <Chip label={label} variant_={variant} size_="small" border />
        </Container>
      ),
      disable: true,
    };
  }

  const updatedFlag = Object.entries(data).find(([key, value]) => key in allFlagTypes && value !== undefined);

  if (!updatedFlag) {
    return {
      subtitle: <StyledText value="Unknown flag change" />,
      disable: true,
    };
  }

  const [flagKey, flagValue] = updatedFlag;
  const {label, variant} = allFlagTypes[flagKey];
  const flagEnabled = flagKey === "military_info" ? (flagValue as MilitaryInfo)?.active : flagValue;
  const IconComponent = flagEnabled ? IconApproved : IconDecline;
  const actionText = flagEnabled ? "added" : "removed";

  const hasExpandableContent = !!(
    creditorRecallData?.recall_reason ||
    creditorRecallData?.recall_status ||
    creditorRecallData?.creditor_recall_status_date ||
    creditorRecallData?.account_resolution ||
    creditorRecallData?.buyback_bank_transaction_id
  );

  return {
    subtitle: (
      <Container row sx={{gap: "4px"}}>
        <IconComponent color={Colors.grey._1000} width={20} height={20} />
        <Chip label={label} variant_={variant} size_="small" border sx={{marginLeft: "2px"}} />
        <StyledText value=" was " />
        <StyledText bold value={actionText} />
      </Container>
    ),
    disable: !hasExpandableContent,
  };
};

export const getConsumerDetailsUpdateSubtitles = (activity: ActivityData): ActivityDetails => {
  const {first_name, middle_name, last_name, date_of_birth, preferred_language, ssn} =
    activity as ConsumerDetailsUpdateData;

  if (middle_name === "") {
    return {
      subtitle: (
        <Container row sx={{gap: "4px"}}>
          <StyledText value="Removed middle name" />
        </Container>
      ),
      disable: true,
    };
  }

  let title = "";
  let value = "";

  if (first_name !== undefined) {
    title = "First name";
    value = first_name;
  } else if (middle_name !== undefined) {
    title = "Middle name";
    value = middle_name;
  } else if (last_name !== undefined) {
    title = "Last Name";
    value = last_name;
  } else if (date_of_birth !== undefined) {
    title = "Date of birth";
    value = dayjs(date_of_birth).format(DATE_FORMAT_V2);
  } else if (preferred_language !== undefined) {
    title = "Preferred language";
    value = preferred_language;
  } else if (ssn !== undefined) {
    title = "SSN";
    value = ssn;
  }

  const subtitle = (
    <Container row sx={{gap: "4px"}}>
      {title && <StyledText value={`${title} changed to:`} />}
      {value && <StyledText bold value={value} />}
    </Container>
  );

  return {subtitle, disable: true};
};

export const getStatuteOfLimitationsUpdateSubtitles = (activity: ActivityData): ActivityDetails => {
  const {statute_barred_date} = activity as StatuteOfLimitationsUpdateData;

  const subtitle = (
    <Container row sx={{gap: "4px"}}>
      <StyledText value={"Statute-barred date changed to: "} />
      {statute_barred_date && <StyledText bold value={dayjs(statute_barred_date).format(DATE_FORMAT_V2)} />}
    </Container>
  );

  return {subtitle, disable: true};
};

export const getRepresentationUpdateSubtitles = (activity: ActivityData): ActivityDetails => {
  const {representation_status, company_name, company_email, company_phone, company_address} =
    activity as RepresentationUpdateData;

  const isUpdateItem =
    company_name !== undefined ||
    company_address !== undefined ||
    company_email !== undefined ||
    company_phone !== undefined;

  const enrolled = representation_status === "enrolled";

  let subtitle: React.ReactNode;

  if (isUpdateItem) {
    const IconComponent = enrolled ? IconApproved : IconDecline;
    subtitle = (
      <Container row sx={{gap: "4px"}}>
        <IconComponent color={Colors.grey._1000} width={20} height={20} />
        <Chip label="Representation" variant_="info" size_="small" border sx={{marginLeft: "2px"}} />
        <StyledText value=" was " />
        <StyledText bold value={enrolled ? "added" : "removed"} />
      </Container>
    );
  } else {
    subtitle = (
      <Container row sx={{gap: "4px"}}>
        <StyledText value="Changed to " />
        <Chip
          label={enrolled ? "Enrolled" : "Not enrolled"}
          variant_={enrolled ? "success" : "error"}
          size_="small"
          border
        />
      </Container>
    );
  }

  return {subtitle, disable: !isUpdateItem};
};

const getDocumentRequestSubtitle = (): ActivityDetails => {
  return {subtitle: null, disable: true};
};

const getDSCSubtitle = (activity: ActivityData): ActivityDetails => {
  const {settlement_offer, discount_rate, max_term, total_balance, first_payment_due, company} = activity as DSCData;
  const subtitle = settlement_offer ? `Settlement offer: ${settlement_offer}` : "";
  const hasExpandableContent =
    !isNil(discount_rate) || !!max_term || !!total_balance || !!first_payment_due || !!company;
  return {subtitle, disable: !hasExpandableContent};
};

const getLegalCollectionsSubtitle = (activity: ActivityData): ActivityDetails => {
  const {event_type, description} = activity as LegalCollectionsData;

  if (event_type === "LEGAL_PRE_LEGAL") {
    return {subtitle: <LegalCollectionSubtitle tagLabel={"Pre Legal"} />, disable: !description};
  }

  if (event_type === "LEGAL_PLACEMENT") {
    return {subtitle: <LegalCollectionSubtitle tagLabel={"Legal Collection"} />, disable: !description};
  }

  if (event_type === "LEGAL_PLACEMENT_TO_VENDOR") {
    const {agency_name, agency_phone_number} = activity as LegalCollectionsData;
    const subtitle = agency_name ? `Assigned to Law firm: ${agency_name}` : "Assigned to Law firm";
    return {subtitle, disable: !description && !agency_phone_number};
  }

  const eventTypeToLabel: Record<string, string> = {
    LEGAL_JUDGMENT: "Judgment Filed",
    LEGAL_REVERTED: "Legal Reverted",
    LEGAL_CLOSED: "Legal Closed",
    LEGAL_SUIT: "Suit Filed",
    AFFIDAVIT_GENERATED_AND_REVIEWED: "Affidavit Generated And Reviewed",
    NY_AFFIRMATION_OF_SALE_GENERATED_AND_REVIEWED: "NY Affirmation Of Sale Generated And Reviewed",
  };

  const subtitle = eventTypeToLabel[event_type] || "";
  return {subtitle, disable: !subtitle || !description};
};
